/* eslint-disable camelcase */
import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';
import type { PLRFormProps } from '../../../../../../types/preloan';
import { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import Header from './components/Header';
import ResultLabels from './components/ResultsLabels';
import NoFilterResults from './components/NoFilterResults';
import { getContactMessage, getRedirectUrl } from '../07_lastStep/components/messageSwitches';
import { getOriginButtonLabel, isMortgageFlow } from '../../formLogic';
import analytics from '../../../../../../utils/analytics';
import ComplementOffer from '../../../ComplementInvite/ComplementOffer';
import ComplementLeadResultDialog from '../../../ComplementInvite/ComplementLeadResultDialog';
import { inviteePlrCategory, LEAD_OFFER_PAGE, leadResultCategory } from '../../../ComplementInvite/analyticsPages';
import CandidateDialog from '../../../ComplementInvite/CandidateDialog';
import HTMLParser from '../../../../../../components/HTMLParser';
import PlrShell, { partnerDisplayName } from '../../../landing/PlrShell';
import { PlrTitle, ProjectLabel } from '../../../landing/ui';
import {
  ComplementCard, DecisionInfo, ReasonsCard, ResultClosing,
} from '../../../landing/PlrResults';
import type { RejectionReason } from '../../../landing/PlrResults';
import {
  CATEGORY_COPY, htmlToText, ReasonCategory, rejectionCategories,
} from '../../../landing/results';
import { partnerFromSchema, useShowHero } from '../../../landing/partner';
import { plr } from '../../../landing/tokens';
import { hasProjectName, projectEntityLabel } from '../../../heroVariant';

const CMF_URL = 'https://conocetudeuda.cmfchile.cl/informe-deudas/629/w4-contents.html';

// [validar textos con Riesgo y Legal]
const todoFor = (category: ReasonCategory | undefined, partnerName: string): React.ReactNode[] => {
  const cmf = (
    <>
      <a href={CMF_URL} target="_blank" rel="noopener noreferrer">
        Revisa tu informe de deudas en la CMF
      </a>
      . Es gratis con tu ClaveÚnica.
    </>
  );
  switch (category) {
    case 'debts':
    case 'punished':
      return [
        cmf,
        'Paga o repacta lo pendiente con quien te cobra.',
        'Vuelve a evaluarte cuando tu registro esté al día.',
      ];
    case 'score':
      return [
        cmf,
        'Mantén tus pagos al día durante los próximos meses.',
        'Vuelve a evaluarte más adelante.',
      ];
    case 'employment':
    case 'gaps':
      return [
        'Mantén tus cotizaciones al día.',
        'Vuelve a evaluarte cuando cumplas más tiempo en tu empleo actual.',
      ];
    default:
      return [`Consulta con ${partnerName} por otras alternativas de financiamiento.`];
  }
};

export default function RejectedForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleEnableLoading,
  } = props;
  const history = useHistory();
  const partner = partnerFromSchema(formSchema);
  const showHero = useShowHero(formSchema);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const mortgageFlow = isMortgageFlow(formSchema.products);

  React.useEffect(() => {
    handleEnableLoading(false);
    const showComplementOffer = formSchema.complementOfferAvailable
      && typeof formSchema.leadComplementAccepted !== 'boolean';
    const leadCategory = leadResultCategory(formSchema.isRedirect);
    if (formSchema.results) {
      const rejectedCategory = inviteePlrCategory(
        formSchema.complementToken,
        formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - RESULTADO',
      );
      formSchema.results.forEach((result) => {
        analytics.page(
          rejectedCategory,
          'USUARIO RECHAZADO',
          {
            trigger: 'backend',
            productType: result.productType,
            risk_models: result.hardFilterResults.risk_models,
            lagunes: result.hardFilterResults.lagunes,
            max_age: result.hardFilterResults.max_age,
            min_age: result.hardFilterResults.min_age,
            punished_debt: result.hardFilterResults.punished_debt,
            alimony_debtor: result.hardFilterResults.alimony_debtor,
            min_emp_length: result.hardFilterResults.min_emp_length,
            min_net_salary: result.hardFilterResults.min_net_salary,
            min_brute_salary: result.hardFilterResults.min_brute_salary,
            leverage_total_debt: result.hardFilterResults.leverage_total_debt,
            max_financial_stress: result.hardFilterResults.max_financial_stress,
            product_availability: result.hardFilterResults.product_availability,
            leverage_consumer_debt: result.hardFilterResults.leverage_consumer_debt,
            min_loan_policy_amount: result.hardFilterResults.min_loan_policy_amount,
            max_dpp_financial_stress: result.hardFilterResults.max_dpp_financial_stress,
            max_mortgage_financial_stress: result.hardFilterResults.max_mortgage_financial_stress,
            protest_and_delinquency_1year: result.hardFilterResults.protest_and_delinquency_1year,
            protest_and_delinquency_5years: result.hardFilterResults.protest_and_delinquency_5years,
            external_score: result.hardFilterResults.external_score,
            pre_required_associated_product_approved: result.hardFilterResults
              .pre_required_associated_product_approved,
          },
        );
      });
    }
    if ((!formSchema.results || formSchema.results?.length === 0) && formSchema.adminComment) {
      const simulatorAvailability = !formSchema.adminComment.includes('capacidades de pago');
      const workerTypeAllowed = !formSchema.adminComment.includes('contrato de trabajo')
      && !formSchema.adminComment.includes('deshabilitada la evaluación');
      const rejectedCategory = inviteePlrCategory(
        formSchema.complementToken,
        formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - RESULTADO',
      );
      analytics.page(
        rejectedCategory,
        'USUARIO RECHAZADO',
        {
          trigger: 'frontend',
          simulator_availability: simulatorAvailability,
          workerType_allowed: workerTypeAllowed,
          admin_rejection: !simulatorAvailability || !workerTypeAllowed,
        },
      );
    }
    if (showComplementOffer) {
      analytics.page(leadCategory, LEAD_OFFER_PAGE, { screen: 'rejected' });
    }
    if (formSchema.origin === 'portal-usuario') {
      setTimeout(() => {
        history.push('/account');
      }, 4000);
      return;
    }
    if (formSchema.config.preLoanRequestConfig?.rejectedStepText?.autoRedirect) {
      setTimeout(() => {
        window.location.href = getRedirectUrl(
          formSchema.config,
          true,
        );
      }, 5000);
    }
  }, []);

  const availableRedirectToDpp = formSchema.partner === 'Banca.Me' && !formSchema.origin.includes('dpp');
  const getRedirectToDppUrl = () => {
    if (formSchema.origin === 'referral' && formSchema.referralCode) {
      return `https://${process.env.REACT_APP_BUCKET_NAME}/preloan?origin=bancame-dpp-referral&partner=Banca.Me&referralCode=${formSchema.referralCode}`;
    }
    return `https://${process.env.REACT_APP_BUCKET_NAME}/preloan?origin=bancame-dpp-rejected-microloan&partner=Banca.Me`;
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={(values) => {
        analytics.track('Click Boton', {
          initiator: 'usuario',
          text: getOriginButtonLabel(formSchema.partner),
        });
        window.location.href = getRedirectUrl(values.config, false);
      }}
    >
      {({
        values,
      }) => {
        if (!mortgageFlow) {
          return (

            <Form>
              <Header
                formSchema={values}
              />
              {formSchema.config.preLoanRequestConfig?.rejectedStepText?.showResults
                ? (
                  <div style={{
                    overflowY: 'auto',
                    maxHeight: '175px',
                    margin: 'auto',
                    backgroundColor: values.config.styles?.backgroundSecondaryColor,
                    marginTop: '10px',
                    borderRadius: '10px',
                  }}
                  >
                    {values.results && values.results.length > 0
                && (
                  <ResultLabels
                    results={values.results}
                    origin={{
                      originLabel: values.origin,
                      partner: values.partner,
                      partnerProducts: values.products,
                    }}
                  />
                )}
                    {(!values.results || values.results?.length === 0)
                && (
                  <NoFilterResults
                    adminComment={values.adminComment}
                  />
                )}
                  </div>
                ) : (
                  <Typography component="div" variant="body2" align="center" style={{ marginTop: 20 }}>
                    Si tienes alguna duda o pregunta,
                    {getContactMessage(formSchema.config)}
                  </Typography>
                )}
              {formSchema.complementOfferAvailable
                && typeof formSchema.leadComplementAccepted !== 'boolean' && (
                <ComplementOffer
                  partner={{
                    partnerName: formSchema.partner,
                    config: formSchema.config,
                    products: formSchema.products,
                  }}
                  token={formSchema.PLRtoken}
                  pageCategory={leadResultCategory(formSchema.isRedirect)}
                  leadApproved={false}
                />
              )}
              <ComplementLeadResultDialog
                leadComplementAccepted={formSchema.leadComplementAccepted}
                ownPreApproved={false}
                leadFirstName={formSchema.complementLeadFirstName}
              />
              {formSchema.config.preLoanRequestConfig?.rejectedStepText?.autoRedirect && (
                <Typography component="div" variant="body2" align="center" style={{ marginTop: 20 }}>
                  Se te redirigirá en breve...
                  {' '}
                  <CircularProgress size={20} />
                </Typography>
              )}
              {availableRedirectToDpp && (
                <Typography component="div" variant="caption" align="center" style={{ marginTop: 25 }}>
                  <Link
                    href={getRedirectToDppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Estoy en una compañia afiliada y quiero evaluarme para Descuento por Planilla.
                  </Link>
                </Typography>
              )}
            </Form>
          );
        }
        const partnerName = partnerDisplayName(partner);
        const complementable = Boolean(formSchema.complementOfferAvailable)
          && typeof formSchema.leadComplementAccepted !== 'boolean';
        const rejectedText = formSchema.config.preLoanRequestConfig?.rejectedStepText;
        const showReasons = Boolean(rejectedText?.showResults);
        // Título y bajada: los que el partner configuró (rejectedStepText), como el flujo
        // anterior. Hay partners que no muestran el rechazo ("¡Muchas gracias!", "Serás
        // contactado…"), así que no se reemplazan por un texto propio.
        const title = htmlToText(rejectedText?.header || '');
        const categories = rejectionCategories(values.results);
        // Todos los motivos (explicación de la decisión automatizada), cada uno con sus
        // acciones. Sin hard filters, el comentario del ejecutivo como único motivo.
        const reasons: RejectionReason[] = categories.length > 0
          ? categories.map((key) => ({
            key, ...CATEGORY_COPY[key], actions: todoFor(key, partnerName),
          }))
          : [{
            key: 'admin',
            title: 'Motivo',
            text: values.adminComment || 'Políticas internas de otorgamiento.',
            actions: todoFor(undefined, partnerName),
          }];
        return (
          <Form style={{ width: '100%' }}>
            <PlrShell
              partner={partner}
              showHero={showHero}
              contentMaxWidth={560}
              testId="plr-result"
            >
              {showHero && (
                <ProjectLabel hideOnPanel>
                  {projectEntityLabel(formSchema.partnerEntityName, formSchema.partner)}
                </ProjectLabel>
              )}
              {title && (
                <PlrTitle size="result" testId="preloan-rejected-title" sx={{ mt: showHero ? '-6px' : 0 }}>
                  {title}
                </PlrTitle>
              )}
              {rejectedText?.body && (
                <Box
                  data-pw="preloan-rejected-body"
                  sx={{
                    m: '-4px 0 0',
                    fontSize: { xs: 15, sm: 17 },
                    lineHeight: 1.5,
                    color: plr.muted,
                    '& p': { m: 0 },
                    '& p + p': { mt: '8px' },
                    '& strong': { color: plr.ink },
                  }}
                >
                  <HTMLParser html={rejectedText.body} />
                </Box>
              )}
              {/* Mismo orden que el flujo anterior: motivos (showResults) o contacto, y después
                la oferta de complemento. */}
              {showReasons ? (
                <ReasonsCard reasons={reasons} />
              ) : (
                <Box component="p" sx={{ m: 0, fontSize: { xs: 13.5, sm: 15 }, color: plr.body }}>
                  {`Si tienes alguna duda o pregunta,${getContactMessage(formSchema.config)}.`}
                </Box>
              )}
              {complementable && (
                <ComplementCard variant="rejected" onOpen={() => setPickerOpen(true)} />
              )}
              {formSchema.config.preLoanRequestConfig?.rejectedStepText?.autoRedirect && (
                <Box
                  component="p"
                  sx={{
                    m: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: 14, color: plr.body,
                  }}
                >
                  Se te redirigirá en breve...
                  <CircularProgress size={16} sx={{ color: plr.ink }} />
                </Box>
              )}
              {/* Sin resultados (showResults=false) el partner no revela el rechazo: no hay una
                evaluación que explicar ni que objetar. */}
              {showReasons && (
                <DecisionInfo
                  project={projectEntityLabel(formSchema.partnerEntityName, formSchema.partner)}
                  hasProject={hasProjectName(formSchema.partnerEntityName)}
                />
              )}
              <ResultClosing backLabel={`Volver a ${partnerName}`} />
            </PlrShell>
            <CandidateDialog
              open={pickerOpen}
              token={formSchema.PLRtoken}
              pageCategory={leadResultCategory(formSchema.isRedirect)}
              onClose={() => setPickerOpen(false)}
            />
            <ComplementLeadResultDialog
              leadComplementAccepted={formSchema.leadComplementAccepted}
              ownPreApproved={false}
              leadFirstName={formSchema.complementLeadFirstName}
            />
          </Form>
        );
      }}
    </Formik>
  );
}

RejectedForm.defaultProps = {
  debts: undefined,
};
